import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Save, ArrowLeft, Search, Upload, Download, X, Check } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import { COLORS } from '../../utils/constants';

const C = COLORS;
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const StudentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  // Search from DB modal states
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchAadhaar, setSearchAadhaar] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [foundStudent, setFoundStudent] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [alreadyLinked, setAlreadyLinked] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  const [formData, setFormData] = useState({
    udise: '',
    school_name: '',
    school_id: '',
    title: 'Mr',
    first_name: '',
    last_name: '',
    gender: 'Male',
    phone: '',
    email: '',
    address: '',
    dob: '',
    father_name: '',
    mother_name: '',
    board: '',
    class: '',
    srn: '',
    aadhaar: '',
    status: 'Active'
  });

  useEffect(() => {
    if (isEdit) {
      fetchStudent();
    }
  }, [id]);

  const fetchStudent = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/students/${id}`);
      if (res.data) {
        setFormData({
          ...res.data,
          school_id: res.data.school_id || '',
          dob: res.data.dob ? res.data.dob.split('T')[0] : '',
          father_name: res.data.father_name || '',
          mother_name: res.data.mother_name || '',
          board: res.data.board || '',
          class: res.data.class || '',
          srn: res.data.srn || '',
          aadhaar: res.data.aadhaar || '',
          status: res.data.status || 'Active'
        });
        if (res.data.profile_photo) {
          setPhotoPreview(`${BASE_URL.replace('/api', '')}${res.data.profile_photo}`);
        }
      }
    } catch (err) {
      toast.error('Failed to load student');
    } finally {
      setLoading(false);
    }
  };

  const handleUdiseSearch = async () => {
    if (!formData.udise) return toast.error('Please enter a UDISE number');
    try {
      const res = await api.get(`/students/udise/${formData.udise}`);
      setFormData(prev => ({ ...prev, school_id: res.data.id, school_name: res.data.name }));
      toast.success('School found');
    } catch (err) {
      setFormData(prev => ({ ...prev, school_id: '', school_name: '' }));
      toast.error('School not found for this UDISE');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.first_name.trim()) {
      return toast.error('First Name is required');
    }
    if (!formData.aadhaar || !formData.aadhaar.trim()) {
      return toast.error('Aadhaar Number is compulsory');
    }
    const cleanAadhaar = formData.aadhaar.trim().replace(/\s+/g, '');
    if (!/^\d{12}$/.test(cleanAadhaar)) {
      return toast.error('Please enter a valid 12-digit Aadhaar Number');
    }
    
    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      submitData.append(key, formData[key]);
    });
    if (photoFile) {
      submitData.append('profile_photo', photoFile);
    }

    try {
      if (isEdit) {
        await api.put(`/students/${id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Student updated successfully');
      } else {
        await api.post('/students', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Student added successfully');
      }
      navigate('/students');
    } catch (err) {
      toast.error('Failed to save student');
    }
  };

  const handleAadhaarSearch = async (e) => {
    if (e) e.preventDefault();
    const clean = searchAadhaar.trim();
    if (!clean) {
      setSearchError('Please enter an Aadhaar card number');
      return;
    }
    try {
      setIsSearching(true);
      setSearchError('');
      setFoundStudent(null);
      const res = await api.get(`/students/search-aadhaar/${encodeURIComponent(clean)}`);
      if (res.data && res.data.student) {
        setFoundStudent(res.data.student);
        setAlreadyLinked(Boolean(res.data.alreadyLinked));
      } else {
        setSearchError('No student found with this Aadhaar number.');
      }
    } catch (err) {
      setSearchError(err.response?.data?.error || 'Student not found with this Aadhaar number.');
      setFoundStudent(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLinkSave = async () => {
    if (!foundStudent) return;
    try {
      setIsLinking(true);
      await api.post('/students/link-existing', { student_id: foundStudent.id });
      toast.success('Student added to your student list successfully!');
      setShowSearchModal(false);
      navigate('/students');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add student to list');
    } finally {
      setIsLinking(false);
    }
  };

  if (loading) return <AppLayout title="Edit Student"><div style={{ padding: 40, textAlign: 'center' }}>Loading...</div></AppLayout>;

  return (
    <AppLayout title={isEdit ? 'Edit Student' : 'Add Student'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <button
          type="button"
          onClick={() => navigate('/students')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 10, cursor: 'pointer', fontWeight: 600, color: C.g700 }}
        >
          <ArrowLeft size={18} /> Back
        </button>

        {!isEdit && (
          <button
            type="button"
            onClick={() => {
              setShowSearchModal(true);
              setSearchAadhaar('');
              setFoundStudent(null);
              setSearchError('');
              setAlreadyLinked(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              background: C.primary,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(79,70,229,0.2)'
            }}
          >
            <Search size={18} /> Search from Db
          </button>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, padding: 32 }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
            
            {/* School Info */}
            <div style={{ gridColumn: '1 / -1' }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: C.g900, marginBottom: 16, paddingBottom: 8, borderBottom: `1px solid ${C.g100}` }}>School Assignment (Optional)</h3>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>UDISE Number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input 
                  type="text" 
                  value={formData.udise} 
                  onChange={e => setFormData({...formData, udise: e.target.value})} 
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} 
                  placeholder="Enter UDISE number" 
                />
                <button type="button" onClick={handleUdiseSearch} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', background: C.g100, border: `1px solid ${C.g200}`, borderRadius: 8, cursor: 'pointer', color: C.g700, fontWeight: 500 }}>
                  <Search size={16} /> Search
                </button>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>School Name</label>
              <input 
                type="text" 
                value={formData.school_name} 
                onChange={e => setFormData({...formData, school_name: e.target.value})} 
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: formData.school_id ? C.g50 : '#fff' }} 
                placeholder="Auto-populated or enter manually" 
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Status</label>
              <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Board</label>
              <select value={formData.board} onChange={e => setFormData({...formData, board: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="">Select Board</option>
                <option value="CBSE">CBSE</option>
                <option value="ICSE">ICSE</option>
                <option value="State Board">State Board</option>
                <option value="IB">IB</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Class</label>
              <select value={formData.class} onChange={e => setFormData({...formData, class: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="">Select Class</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(c => (
                  <option key={c} value={`Class ${c}`}>Class {c}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>SRN Number</label>
              <input 
                type="text" 
                value={formData.srn} 
                onChange={e => setFormData({...formData, srn: e.target.value})} 
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} 
                placeholder="Enter SRN number" 
              />
            </div>

            {/* Personal Info */}
            <div style={{ gridColumn: '1 / -1', marginTop: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: C.g900, marginBottom: 16, paddingBottom: 8, borderBottom: `1px solid ${C.g100}` }}>Personal Details</h3>
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 24, marginBottom: 8 }}>
              <div style={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: C.g100, border: `1px dashed ${C.g300}`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Upload size={24} color={C.g400} />
                )}
              </div>
              <div>
                <label style={{ display: 'inline-block', padding: '8px 16px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: C.g700 }}>
                  Upload Photo
                  <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
                </label>
                <div style={{ fontSize: 12, color: C.g500, marginTop: 8 }}>Square image recommended (JPG, PNG)</div>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Title</label>
              <select value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="Mr">Mr</option>
                <option value="Ms">Ms</option>
                <option value="Mrs">Mrs</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Gender</label>
              <select value={formData.gender} onChange={e => setFormData({...formData, gender: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>First Name *</label>
              <input type="text" value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="First name" required />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Last Name</label>
              <input type="text" value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Last name" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Date of Birth</label>
              <input type="date" value={formData.dob} onChange={e => setFormData({...formData, dob: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Father Name</label>
              <input type="text" value={formData.father_name} onChange={e => setFormData({...formData, father_name: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Father's name" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Mother Name</label>
              <input type="text" value={formData.mother_name} onChange={e => setFormData({...formData, mother_name: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Mother's name" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>
                Aadhaar Number <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input 
                type="text" 
                value={formData.aadhaar} 
                onChange={e => setFormData({...formData, aadhaar: e.target.value})} 
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} 
                placeholder="Enter 12-digit Aadhaar number" 
                maxLength={14}
                required 
              />
            </div>

            {/* Contact Info */}
            <div style={{ gridColumn: '1 / -1', marginTop: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: C.g900, marginBottom: 16, paddingBottom: 8, borderBottom: `1px solid ${C.g100}` }}>Contact Info</h3>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Phone Number</label>
              <input type="text" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Phone number" />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Email Address</label>
              <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}` }} placeholder="Email address" />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: C.g700 }}>Address</label>
              <textarea value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.g200}`, minHeight: 80, resize: 'vertical' }} placeholder="Full address" />
            </div>

          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 32, paddingTop: 24, borderTop: `1px solid ${C.g100}` }}>
            <button
              type="button"
              onClick={() => navigate('/students')}
              style={{ padding: '12px 24px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 10, cursor: 'pointer', fontWeight: 600, color: C.g700 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)' }}
            >
              <Save size={18} /> {isEdit ? 'Update Student' : 'Save Student'}
            </button>
          </div>
        </form>
      </div>

      {/* Search from DB Modal */}
      {showSearchModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16
          }}
          onClick={() => setShowSearchModal(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              maxWidth: 560,
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              overflow: 'hidden'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px 24px',
                borderBottom: `1px solid ${C.g200}`,
                background: C.g50
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(79, 70, 229, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.primary }}>
                  <Search size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: C.g900 }}>Search Student from DB</h3>
                  <p style={{ margin: 0, fontSize: 13, color: C.g500 }}>Search existing student by Aadhaar card number</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowSearchModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.g400, padding: 4, borderRadius: 6, display: 'flex' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
              <form onSubmit={handleAadhaarSearch} style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 600, color: C.g700 }}>
                  Aadhaar Card Number
                </label>
                <div style={{ display: 'flex', gap: 10 }}>
                  <input
                    type="text"
                    value={searchAadhaar}
                    onChange={e => setSearchAadhaar(e.target.value)}
                    placeholder="Enter 12-digit Aadhaar number"
                    autoFocus
                    style={{
                      flex: 1,
                      padding: '12px 14px',
                      borderRadius: 10,
                      border: `1px solid ${C.g300}`,
                      fontSize: 14,
                      outline: 'none',
                      fontFamily: 'monospace',
                      letterSpacing: 1
                    }}
                  />
                  <button
                    type="submit"
                    disabled={isSearching}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '0 20px',
                      background: C.primary,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      cursor: isSearching ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      fontSize: 14,
                      opacity: isSearching ? 0.7 : 1
                    }}
                  >
                    <Search size={16} /> {isSearching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </form>

              {/* Error Message */}
              {searchError && (
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: 10,
                    background: '#FEF2F2',
                    border: '1px solid #FCA5A5',
                    color: '#991B1B',
                    fontSize: 14,
                    marginBottom: 16
                  }}
                >
                  {searchError}
                </div>
              )}

              {/* Student Detail View */}
              {foundStudent && (
                <div
                  style={{
                    borderRadius: 12,
                    border: `1px solid ${C.g200}`,
                    background: '#FAFAFA',
                    padding: 20,
                    marginTop: 10
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                    <div
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: '#E0E7FF',
                        color: C.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                        fontWeight: 700,
                        overflow: 'hidden',
                        flexShrink: 0
                      }}
                    >
                      {foundStudent.profile_photo ? (
                        <img
                          src={`${BASE_URL.replace('/api', '')}${foundStudent.profile_photo}`}
                          alt="Student"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        `${foundStudent.first_name?.[0] || ''}${foundStudent.last_name?.[0] || ''}`
                      )}
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.g900 }}>
                        {foundStudent.title ? `${foundStudent.title} ` : ''}{foundStudent.first_name} {foundStudent.last_name}
                      </h4>
                      <p style={{ margin: '4px 0 0 0', fontSize: 13, color: C.g600 }}>
                        {foundStudent.class || 'No Class'} {foundStudent.board ? `• ${foundStudent.board}` : ''}
                      </p>
                      {foundStudent.school_name && (
                        <p style={{ margin: '2px 0 0 0', fontSize: 12, color: C.g500 }}>
                          🏫 {foundStudent.school_name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 12,
                      fontSize: 13,
                      padding: 14,
                      background: '#fff',
                      borderRadius: 8,
                      border: `1px solid ${C.g100}`
                    }}
                  >
                    <div>
                      <span style={{ color: C.g500 }}>Aadhaar:</span>{' '}
                      <strong style={{ color: C.g800 }}>{foundStudent.aadhaar || 'N/A'}</strong>
                    </div>
                    <div>
                      <span style={{ color: C.g500 }}>Gender:</span>{' '}
                      <strong style={{ color: C.g800 }}>{foundStudent.gender || 'N/A'}</strong>
                    </div>
                    <div>
                      <span style={{ color: C.g500 }}>Father:</span>{' '}
                      <strong style={{ color: C.g800 }}>{foundStudent.father_name || 'N/A'}</strong>
                    </div>
                    <div>
                      <span style={{ color: C.g500 }}>Mother:</span>{' '}
                      <strong style={{ color: C.g800 }}>{foundStudent.mother_name || 'N/A'}</strong>
                    </div>
                    <div>
                      <span style={{ color: C.g500 }}>Phone:</span>{' '}
                      <strong style={{ color: C.g800 }}>{foundStudent.phone || 'N/A'}</strong>
                    </div>
                    <div>
                      <span style={{ color: C.g500 }}>Status:</span>{' '}
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          background: foundStudent.status === 'Active' ? '#DEF7EC' : '#FDE8E8',
                          color: foundStudent.status === 'Active' ? '#03543F' : '#9B1C1C'
                        }}
                      >
                        {foundStudent.status || 'Active'}
                      </span>
                    </div>
                  </div>

                  {alreadyLinked ? (
                    <div
                      style={{
                        marginTop: 16,
                        padding: '10px 14px',
                        borderRadius: 8,
                        background: '#EFF6FF',
                        border: '1px solid #BFDBFE',
                        color: '#1E40AF',
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <Check size={16} /> This student is already in your student list.
                    </div>
                  ) : (
                    <div
                      style={{
                        marginTop: 16,
                        padding: '10px 14px',
                        borderRadius: 8,
                        background: '#ECFDF5',
                        border: '1px solid #A7F3D0',
                        color: '#065F46',
                        fontSize: 13
                      }}
                    >
                      ✓ Student record found! Click "Save & Add to My Students" below to link this student.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 12,
                padding: '16px 24px',
                borderTop: `1px solid ${C.g200}`,
                background: C.g50
              }}
            >
              <button
                type="button"
                onClick={() => setShowSearchModal(false)}
                style={{
                  padding: '10px 18px',
                  background: '#fff',
                  border: `1px solid ${C.g200}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  color: C.g700
                }}
              >
                Close
              </button>
              {foundStudent && !alreadyLinked && (
                <button
                  type="button"
                  onClick={handleLinkSave}
                  disabled={isLinking}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 20px',
                    background: '#10B981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    cursor: isLinking ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    fontSize: 14,
                    boxShadow: '0 4px 12px rgba(16,185,129,0.25)',
                    opacity: isLinking ? 0.7 : 1
                  }}
                >
                  <Save size={16} /> {isLinking ? 'Saving...' : 'Save & Add to My Students'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default StudentForm;
